import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, CheckCircle2Icon, QrCodeIcon, Image as ImageIcon, StoreIcon } from 'lucide-react';
import { useRaastTheme, slideVariants } from './RaastShared';
interface RaastQrTillProps {
  onBack: () => void;
  onHome: () => void;
  onTransfer?: () => void;
}
type Step = 'scan' | 'entry' | 'review' | 'otp' | 'success';
export function RaastQrTill({
  onBack,
  onHome,
  onTransfer
}: RaastQrTillProps) {
  const {
    bgStyle,
    cardBg,
    inputBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('scan');
  const [direction, setDirection] = useState(1);
  const [tillNumber, setTillNumber] = useState('');
  const [amount, setAmount] = useState('');
  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  useEffect(() => {
    if (step === 'otp') {
      const interval = setInterval(() => {
        setTimer(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`qr-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (newOtp.every(v => v !== '')) {
      setTimeout(() => navigateTo('success'), 350);
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`qr-otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between z-10 relative" data-id="element-2311">
      <button onClick={onBackClick} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2312">
        <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2313" />
      </button>
      <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2314">{title}</h1>
      <button onClick={onHome} className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${isDark ? 'border-[#F37024] text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#5A1408] text-[#5A1408] hover:bg-[#5A1408]/5'}`} data-id="element-2315">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2316" />
      </button>
    </div>;
  const renderScan = () => <motion.div key="scan" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2317">
      {renderHeader('Scan QR', onBack)}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8" data-id="element-2318">
        <div className="relative w-64 h-64 mb-8" data-id="element-2319">
          {/* Scanner Frame */}
          <div className="absolute inset-0 border-2 border-[#F37024]/30 rounded-3xl overflow-hidden" data-id="element-2320">
            <div className="w-full h-full bg-black/5" data-id="element-2321" />
            {/* Animated scanning line */}
            <motion.div animate={{
            top: ['0%', '100%', '0%']
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} className="absolute left-0 right-0 h-1 bg-[#F37024] shadow-[0_0_10px_#F37024]" data-id="element-2322" />
          </div>
          {/* Corner markers */}
          <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-[#F37024] rounded-tl-3xl" data-id="element-2323" />
          <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[#F37024] rounded-tr-3xl" data-id="element-2324" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[#F37024] rounded-bl-3xl" data-id="element-2325" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-[#F37024] rounded-br-3xl" data-id="element-2326" />
        </div>
        <p className={`text-[14px] text-center mb-8 ${textSec}`} data-id="element-2327">
          Align QR code within the frame to scan
        </p>

        <div className="w-full flex gap-4" data-id="element-2328">
          <button onClick={() => navigateTo('entry')} className={`flex-1 py-4 rounded-2xl font-bold text-[14px] flex flex-col items-center justify-center gap-2 border ${cardBg} ${borderClr} ${textPri}`} data-id="element-2329">
            <StoreIcon className="w-6 h-6 text-[#F37024]" data-id="element-2330" />
            Enter Till Number
          </button>
          <button onClick={() => navigateTo('entry')} className={`flex-1 py-4 rounded-2xl font-bold text-[14px] flex flex-col items-center justify-center gap-2 border ${cardBg} ${borderClr} ${textPri}`} data-id="element-2331">
            <ImageIcon className="w-6 h-6 text-[#F37024]" data-id="element-2332" />
            Select from Gallery
          </button>
        </div>

        {onTransfer && <button onClick={onTransfer} className="mt-6 text-[14px] font-bold text-[#F37024] underline-offset-2 hover:underline" data-id="element-2333">
            Send to a person instead
          </button>}
      </div>
    </motion.div>;
  const renderEntry = () => <motion.div key="entry" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2334">
      {renderHeader('Payment Details', () => navigateTo('scan', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2335">
        <div className="space-y-6 mb-auto" data-id="element-2336">
          <div data-id="element-2337">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2338">
              Till Number / Merchant ID
            </label>
            <input type="text" value={tillNumber} onChange={e => setTillNumber(e.target.value)} placeholder="Enter Till Number" className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2339" />
          </div>
          <div data-id="element-2340">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2341">
              Amount (PKR)
            </label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2342" />
          </div>
        </div>
        <button onClick={() => navigateTo('review')} disabled={!tillNumber || !amount} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md disabled:opacity-50 mt-6" data-id="element-2343">
          Next
        </button>
      </div>
    </motion.div>;
  const renderReview = () => <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2344">
      {renderHeader('Review Payment', () => navigateTo('entry', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2345">
        <div className={`rounded-[24px] p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2346">
          <div className="flex flex-col items-center mb-8 text-center" data-id="element-2347">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2348">
              <QrCodeIcon className="w-8 h-8 text-[#F37024]" data-id="element-2349" />
            </div>
            <p className={`text-[28px] font-bold ${textPri}`} data-id="element-2350">
              Rs. {parseInt(amount || '0').toLocaleString()}
            </p>
            <p className={`text-[14px] mt-1 ${textSec}`} data-id="element-2351">
              To Till: {tillNumber}
            </p>
          </div>
          <div className="space-y-4" data-id="element-2352">
            <div className="flex justify-between" data-id="element-2353">
              <span className={textSec} data-id="element-2354">Fee</span>
              <span className={`font-bold ${textPri}`} data-id="element-2355">Rs. 0</span>
            </div>
            <div className={`h-px w-full ${borderClr} my-2`} data-id="element-2356" />
            <div className="flex justify-between" data-id="element-2357">
              <span className={`font-bold ${textPri}`} data-id="element-2358">Total Amount</span>
              <span className={`font-bold text-[#F37024]`} data-id="element-2359">
                Rs. {parseInt(amount || '0').toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        <button onClick={() => navigateTo('otp')} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md mt-6" data-id="element-2360">
          Confirm Payment
        </button>
      </div>
    </motion.div>;
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2361">
      {renderHeader('Enter OTP', () => navigateTo('review', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2362">
        <div className="mb-8 text-center" data-id="element-2363">
          <p className={`text-[14px] ${textSec}`} data-id="element-2364">
            We've sent a 6-digit code to your registered mobile number
          </p>
        </div>
        <div className="flex justify-between gap-2 mb-8" data-id="element-2365">
          {otp.map((digit, i) => <input key={i} id={`qr-otp-${i}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 rounded-xl text-center text-[24px] font-bold border-2 outline-none transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10 text-[#F37024]' : `${borderClr} ${cardBg} ${textPri} focus:border-[#F37024]/50`}`} data-id="element-2366" />)}
        </div>
        <div className="text-center mb-auto" data-id="element-2367">
          {timer > 0 ? <p className={`text-[14px] font-medium ${textSec}`} data-id="element-2368">
              Resend OTP in 0:{timer.toString().padStart(2, '0')}
            </p> : <button onClick={() => setTimer(30)} className="text-[14px] font-bold text-[#F37024]" data-id="element-2369">
              Resend OTP
            </button>}
        </div>
      </div>
    </motion.div>;
  const renderSuccess = () => <motion.div key="success" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2370">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12" data-id="element-2371">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        damping: 15
      }} className="w-24 h-24 bg-[#22C55E] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-[#22C55E]/30" data-id="element-2372">
          <CheckCircle2Icon className="w-12 h-12 text-white" strokeWidth={2.5} data-id="element-2373" />
        </motion.div>
        <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`text-2xl font-bold mb-2 text-center ${textPri}`} data-id="element-2374">
          Payment Successful!
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`text-[15px] text-center mb-8 ${textSec}`} data-id="element-2375">
          Your payment of{' '}
          <span className="font-bold text-[#F37024]" data-id="element-2376">
            Rs. {parseInt(amount || '0').toLocaleString()}
          </span>{' '}
          to Till {tillNumber} has been completed.
        </motion.p>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`w-full rounded-[24px] p-6 shadow-sm border ${cardBg} ${borderClr}`} data-id="element-2377">
          <div className="space-y-4" data-id="element-2378">
            <div className="flex justify-between" data-id="element-2379">
              <span className={textSec} data-id="element-2380">Reference ID</span>
              <span className={`font-bold ${textPri}`} data-id="element-2381">
                RST-{Math.floor(Math.random() * 1000000000)}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2382">
              <span className={textSec} data-id="element-2383">Date & Time</span>
              <span className={`font-bold ${textPri}`} data-id="element-2384">
                {new Date().toLocaleString()}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
      <div className="px-6 pb-8" data-id="element-2385">
        <button onClick={onHome} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-2386">
          Done
        </button>
      </div>
    </motion.div>;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2387">
      {step === 'scan' && renderScan()}
      {step === 'entry' && renderEntry()}
      {step === 'review' && renderReview()}
      {step === 'otp' && renderOtp()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}